import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import LogoutConfirmation from "../../components/LogoutConfirmation";
import PortalPopup from "../../components/PortalPopup";
import NavigationPanel from "../../components/NavigationPanel";
import Footer from "../../components/Footer";
import SettingNav from "../../components/settingNav";
import { GalleryExport, Camera } from "iconsax-reactjs";
import TabState from "../../components/TabState";
import ToastMsg from "../../components/ToastMsg";
import { userService } from "../../services/userService";

const API_URL = process.env.REACT_APP_API_URL;

const SettingsAccountPage = () => {
  const navigate = useNavigate();
  const [isLogoutConfirmationOpen, setLogoutConfirmationOpen] = useState(false);
  const [isToastMsgOpen, setToastMsgOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState(
    "Changes saved successfully",
  );

  // Form states
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [timezone, setTimezone] = useState("British Summer Time (BST)");
  const [role, setRole] = useState("");
  const [profileImage, setProfileImage] = useState("");
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [isCompanyOwner, setIsCompanyOwner] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState("");

  const fileInputRef = useRef(null);

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      setIsLoading(true);
      const result = await userService.getMe();
      const nameParts = (result.name || "").split(" ");
      setFirstName(nameParts[0] || "");
      setEmail(result.email || "");
      setTimezone(result.timezone || "British Summer Time (BST)");
      setRole(result.isCompanyOwner ? "Admin" : "User");
      setIsCompanyOwner(result.isCompanyOwner || false);
      setProfileImage(
        result.profileImage ? `${API_URL}${result.profileImage}` : "",
      );
    } catch (err) {
      console.error("Failed to load user data:", err);
      setError("Failed to load user data");
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageUpload = async (file) => {
    try {
      setIsUploading(true);
      setError("");
      const token = localStorage.getItem("authToken");
      if (!token) {
        setError("Not authenticated");
        navigate("/login");
        return;
      }
      const formData = new FormData();
      formData.append("profileImage", file);
      const response = await fetch(
        `${API_URL}/api/users/upload-profile-image`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        },
      );
      const result = await response.json();
      if (!response.ok)
        throw new Error(result.message || "Failed to upload image");
      if (result.profileImageUrl) {
        const fullImageUrl = `${API_URL}${result.profileImageUrl}`;
        setProfileImage(fullImageUrl);
        setProfileImageFile(null);
        const cachedUser = JSON.parse(localStorage.getItem("authUser") || "{}");
        cachedUser.profileImage = result.profileImageUrl;
        localStorage.setItem("authUser", JSON.stringify(cachedUser));
        userService._cachedUser = cachedUser;
        return fullImageUrl;
      }
    } catch (err) {
      console.error("Image upload error:", err);
      throw new Error(err.message || "Failed to upload image");
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const validTypes = [
      "image/jpeg",
      "image/png",
      "image/jpg",
      "image/svg+xml",
      "image/gif",
      "image/webp",
    ];
    if (!validTypes.includes(file.type)) {
      setError("Please upload a valid image file (JPEG, PNG, SVG, GIF, WebP)");
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      setError("File size should be less than 20MB");
      return;
    }
    setProfileImageFile(file);
    setError("");
  };

  const handleRemoveImage = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;
      const response = await fetch(
        `${API_URL}/api/users/remove-profile-image`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      const result = await response.json();
      if (!response.ok)
        throw new Error(result.message || "Failed to remove profile image");
      setProfileImage("");
      setProfileImageFile(null);
      const cachedUser = JSON.parse(localStorage.getItem("authUser") || "{}");
      cachedUser.profileImage = "";
      localStorage.setItem("authUser", JSON.stringify(cachedUser));
      userService._cachedUser = cachedUser;
      setToastMessage("Profile image removed successfully");
      openToastMsg();
    } catch (err) {
      setError(err.message || "Failed to remove profile image");
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError("");
      if (!firstName.trim()) {
        setError("First name is required");
        return;
      }
      const token = localStorage.getItem("authToken");
      if (!token) {
        setError("Not authenticated");
        navigate("/login");
        return;
      }
      let updatedProfileImage = profileImage;
      if (profileImageFile) {
        try {
          updatedProfileImage = await handleImageUpload(profileImageFile);
        } catch (uploadError) {
          setError(uploadError.message);
          setIsSaving(false);
          return;
        }
      }
      const profileImagePath = updatedProfileImage
        ? updatedProfileImage.replace(API_URL, "")
        : "";
      const updatePayload = {
        name: `${firstName.trim()}`,
        timezone: timezone,
        role: role,
        profileImage: profileImagePath,
      };
      const response = await fetch(`${API_URL}/api/users/update-profile`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updatePayload),
      });
      const result = await response.json();
      if (!response.ok)
        throw new Error(result.message || "Failed to update profile");
      if (result.user) {
        localStorage.setItem("authUser", JSON.stringify(result.user));
        userService._cachedUser = result.user;
      }
      setToastMessage("Changes saved successfully");
      openToastMsg();
    } catch (err) {
      console.error("Save error:", err);
      setError(err.message || "Failed to save changes");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    loadUserData();
    setError("");
    setProfileImageFile(null);
  };

  const closeLogoutConfirmation = useCallback(
    () => setLogoutConfirmationOpen(false),
    [],
  );
  const openToastMsg = useCallback(() => setToastMsgOpen(true), []);
  const closeToastMsg = useCallback(() => setToastMsgOpen(false), []);

  const handleDrop = (event) => {
    event.preventDefault();
    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type.startsWith("image/")) {
        if (file.size > 20 * 1024 * 1024) {
          setError("File size should be less than 20MB");
          return;
        }
        setProfileImageFile(file);
        setError("");
      } else {
        setError("Please upload an image file");
      }
    }
  };

  const handleDragOver = (event) => event.preventDefault();

  const tabs = [
    { label: "Profile", to: "/setting-account" },
    { label: "Connected accounts", to: "/setting-account2" },
    { label: "Security", to: "/setting-account3" },
    { label: "Delete account", to: "/setting-account4" },
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,400&display=swap');

        html, body { background: rgb(241, 245, 249) !important; }

        .dd-root * { box-sizing: border-box; }
        .dd-root {
          font-family: 'DM Sans', sans-serif;
          background: rgb(241, 245, 249) !important;
          min-height: 100vh;
        }
        .dd-root.bg-white,
        .dd-root.bg-neutrals-neutral-50,
        .dd-root.bg-gray-50 {
          background: rgb(241, 245, 249) !important;
        }

        .dd-card {
          background: #ffffff;
          border: 1px solid #E2E8F0;
          border-radius: 16px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 2px 12px rgba(0,0,0,0.04);
          transition: box-shadow 0.2s ease;
          position: relative;
          overflow: hidden;
        }

        .dd-section-divider {
          border: none;
          border-top: 1px solid #E2E8F0;
          margin: 0;
        }

        .dd-section-label {
          font-size: 14px;
          font-weight: 700;
          color: #0F172A;
          margin: 0 0 12px 0;
        }

        .dd-section-sublabel {
          font-size: 13px;
          color: #64748B;
          margin: 2px 0 0 0;
        }

        .dd-btn-secondary {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 8px 18px;
          background: #ffffff; border: 1px solid #E2E8F0; border-radius: 8px;
          font-family: 'DM Sans', sans-serif;
          font-size: 13px; font-weight: 600; color: #0F172A;
          cursor: pointer; box-shadow: 0 1px 2px rgba(0,0,0,0.05);
          transition: border-color 0.15s, background 0.15s;
        }
        .dd-btn-secondary:hover { border-color: #94A3B8; background: #F8FAFC; }
        .dd-btn-secondary:disabled { opacity: 0.6; cursor: not-allowed; }

        .dd-btn-primary {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 8px 18px;
          background: #2563EB; border: 1px solid #2563EB; border-radius: 8px;
          font-family: 'DM Sans', sans-serif;
          font-size: 13px; font-weight: 600; color: #ffffff;
          cursor: pointer;
          transition: background 0.15s, box-shadow 0.15s;
        }
        .dd-btn-primary:hover { background: #1D4ED8; box-shadow: 0 4px 12px rgba(37,99,235,0.25); }
        .dd-btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }

        .dd-input {
          width: 100%;
          padding: 12px 14px;
          border: 1px solid #E2E8F0;
          border-radius: 8px;
          font-family: 'DM Sans', sans-serif;
          font-size: 14px;
          color: #1E293B;
          background: #FFFFFF;
          outline: none;
          transition: border-color 0.15s;
        }
        .dd-input:focus { border-color: #2563EB; }
        .dd-input:disabled { background: #F8FAFC; color: #64748B; cursor: not-allowed; }

        .dd-upload-area {
          border: 2px dashed #E2E8F0;
          border-radius: 12px;
          padding: 24px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          transition: border-color 0.15s, background 0.15s;
          background: #F8FAFC;
        }
        .dd-upload-area:hover { border-color: #2563EB; background: #F1F5FF; }
        .dd-upload-area.dragover { border-color: #2563EB; background: #EFF6FF; }
      `}</style>

      <div
        className="dd-root w-full overflow-visible"
        style={{ background: "rgb(241, 245, 249)", minHeight: "100vh" }}
      >
        <NavigationPanel />

        <div
          style={{
            position: "absolute",
            top: 136,
            width: "100%",
            padding: "0 96px 64px",
            display: "flex",
            flexDirection: "column",
            gap: 24,
          }}
        >
          {/* Page heading */}
          <div style={{ borderBottom: "1px solid #E2E8F0", paddingBottom: 24 }}>
            <h1
              style={{
                margin: 0,
                fontSize: 22,
                fontWeight: 700,
                color: "#0F172A",
              }}
            >
              Settings
            </h1>
            <p style={{ margin: "4px 0 0", fontSize: 14, color: "#64748B" }}>
              Get your billing information, generate/review invoices.
            </p>
          </div>

          <div style={{ display: "flex", alignItems: "flex-start", gap: 32 }}>
            <SettingNav />

            <div
              className="dd-card"
              style={{
                flex: 1,
                padding: 28,
                display: "flex",
                flexDirection: "column",
                gap: 24,
              }}
            >
              {/* Card header */}
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  paddingBottom: 20,
                  borderBottom: "1px solid #E2E8F0",
                }}
              >
                <div>
                  <div
                    style={{ fontSize: 16, fontWeight: 700, color: "#0F172A" }}
                  >
                    Account
                  </div>
                  <div style={{ fontSize: 13, color: "#64748B", marginTop: 2 }}>
                    Allow users to configure how and when communication is
                    initiated with debtors.
                  </div>
                </div>
                <div style={{ display: "flex", gap: 10, flexShrink: 0 }}>
                  <button
                    className="dd-btn-secondary"
                    onClick={handleCancel}
                    disabled={isSaving || isUploading}
                  >
                    Cancel
                  </button>
                  <button
                    className="dd-btn-primary"
                    onClick={handleSave}
                    disabled={isSaving || isUploading}
                  >
                    {isSaving ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>

              {/* Error message */}
              {error && (
                <div
                  style={{
                    padding: "12px 16px",
                    background: "#FEF2F2",
                    border: "1px solid #FECACA",
                    borderRadius: 8,
                    color: "#991B1B",
                    fontSize: 13,
                  }}
                >
                  {error}
                </div>
              )}

              {/* Loading state */}
              {isLoading ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    padding: 40,
                  }}
                >
                  <p style={{ color: "#64748B" }}>Loading...</p>
                </div>
              ) : (
                <>
                  {/* Sub-tabs */}
                  <div style={{ display: "flex", gap: 8, marginBottom: -8 }}>
                    {tabs.map((tab) => (
                      <TabState key={tab.to} label={tab.label} to={tab.to} />
                    ))}
                  </div>

                  <hr className="dd-section-divider" />

                  {/* Full Name */}
                  <div
                    style={{ display: "flex", flexDirection: "column", gap: 6 }}
                  >
                    <label className="dd-section-label" style={{ margin: 0 }}>
                      Full Name <span style={{ color: "#EF4444" }}>*</span>
                    </label>
                    <input
                      type="text"
                      className="dd-input"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="Enter your full name"
                    />
                  </div>

                  <hr className="dd-section-divider" />

                  {/* Email */}
                  <div
                    style={{ display: "flex", flexDirection: "column", gap: 6 }}
                  >
                    <label className="dd-section-label" style={{ margin: 0 }}>
                      Email address <span style={{ color: "#EF4444" }}>*</span>
                    </label>
                    <input
                      type="email"
                      className="dd-input"
                      value={email}
                      disabled
                      style={{ cursor: "not-allowed" }}
                    />
                    <p className="dd-section-sublabel">
                      Email cannot be changed
                    </p>
                  </div>

                  <hr className="dd-section-divider" />

                  {/* Profile Image */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 12,
                    }}
                  >
                    <label className="dd-section-label" style={{ margin: 0 }}>
                      Profile photo
                    </label>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 20,
                      }}
                    >
                      {/* Preview */}
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <div style={{ position: "relative" }}>
                          {profileImage || profileImageFile ? (
                            <div>
                              <img
                                src={
                                  profileImageFile
                                    ? URL.createObjectURL(profileImageFile)
                                    : profileImage
                                }
                                alt="Profile preview"
                                style={{
                                  width: 80,
                                  height: 80,
                                  borderRadius: "50%",
                                  objectFit: "cover",
                                  border: "2px solid #E2E8F0",
                                }}
                              />
                              {isUploading && (
                                <div
                                  style={{
                                    position: "absolute",
                                    inset: 0,
                                    borderRadius: "50%",
                                    background: "rgba(0,0,0,0.4)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <div
                                    style={{
                                      width: 24,
                                      height: 24,
                                      border: "3px solid rgba(255,255,255,0.3)",
                                      borderTopColor: "#fff",
                                      borderRadius: "50%",
                                      animation: "spin 0.8s linear infinite",
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                          ) : (
                            <div
                              style={{
                                width: 80,
                                height: 80,
                                borderRadius: "50%",
                                background: "#F1F5F9",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <Camera size={28} color="#94A3B8" />
                            </div>
                          )}
                        </div>
                        {(profileImage || profileImageFile) && !isUploading && (
                          <button
                            onClick={handleRemoveImage}
                            style={{
                              background: "none",
                              border: "none",
                              color: "#EF4444",
                              fontSize: 12,
                              fontWeight: 600,
                              cursor: "pointer",
                            }}
                            disabled={isSaving}
                          >
                            Remove
                          </button>
                        )}
                      </div>

                      {/* Upload area */}
                      <div
                        className="dd-upload-area"
                        style={{ flex: 1 }}
                        onClick={() => fileInputRef.current?.click()}
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                      >
                        <input
                          type="file"
                          ref={fileInputRef}
                          style={{ display: "none" }}
                          accept="image/*"
                          onChange={handleFileSelect}
                          disabled={isUploading}
                        />
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 40,
                            height: 40,
                            borderRadius: 8,
                            background: "#FFFFFF",
                            border: "1px solid #E2E8F0",
                          }}
                        >
                          {isUploading ? (
                            <div
                              style={{
                                width: 20,
                                height: 20,
                                border: "2px solid #CBD5E1",
                                borderTopColor: "#2563EB",
                                borderRadius: "50%",
                                animation: "spin 0.8s linear infinite",
                              }}
                            />
                          ) : (
                            <GalleryExport size={20} color="#64748B" />
                          )}
                        </div>
                        <div style={{ textAlign: "center" }}>
                          <span style={{ fontWeight: 600, color: "#0F172A" }}>
                            Click to upload
                          </span>
                          <span style={{ color: "#64748B" }}>
                            {" "}
                            or drag and drop
                          </span>
                        </div>
                        <p
                          style={{ fontSize: 12, color: "#94A3B8", margin: 0 }}
                        >
                          SVG, PNG, JPG, GIF, WebP (max. 20MB)
                        </p>
                        {profileImageFile && !isUploading && (
                          <p
                            style={{
                              fontSize: 12,
                              color: "#16A34A",
                              margin: 0,
                            }}
                          >
                            Selected: {profileImageFile.name}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <hr className="dd-section-divider" />

                  {/* Role */}
                  <div
                    style={{ display: "flex", flexDirection: "column", gap: 6 }}
                  >
                    <label className="dd-section-label" style={{ margin: 0 }}>
                      Role
                    </label>
                    <div
                      style={{
                        padding: "12px 14px",
                        border: "1px solid #E2E8F0",
                        borderRadius: 8,
                        background: "#F8FAFC",
                        fontSize: 14,
                        color: "#1E293B",
                      }}
                    >
                      {isCompanyOwner ? "Admin" : "User"}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
          <Footer />
        </div>
      </div>

      {isLogoutConfirmationOpen && (
        <PortalPopup
          overlayColor="rgba(113, 113, 113, 0.3)"
          placement="Centered"
          onOutsideClick={closeLogoutConfirmation}
        >
          <LogoutConfirmation onClose={closeLogoutConfirmation} />
        </PortalPopup>
      )}
      {isToastMsgOpen && (
        <PortalPopup
          overlayColor="rgba(113, 113, 113, 0.3)"
          placement="Centered"
          onOutsideClick={closeToastMsg}
        >
          <ToastMsg onClose={closeToastMsg} message={toastMessage} />
        </PortalPopup>
      )}
    </>
  );
};

export default SettingsAccountPage;
